"use client";

import { Briefcase, Building2, Check, ArrowRight, ShieldCheck, Users, HelpCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Link from "next/link";
import { motion } from "framer-motion";
import BottomCta from "@/components/BottomCta";

const OFFERS = [
  { title: "Flexible Workforce", desc: "Solutions tailored precisely to your operational needs." },
  { title: "Skilled & Semi-Skilled", desc: "Qualified manpower trained for precision tasks." },
  { title: "General Labour Supply", desc: "Dependable general workforce for immediate deployment." },
  { title: "Project-Based Deployment", desc: "Scalable teams for short or long-term contracts." },
  { title: "Bulk Hiring Solutions", desc: "Efficient mass recruitment without compromising quality." },
  { title: "Pre-Screened Candidates", desc: "Every profile is thoroughly verified and vetted." }
];

const INDUSTRIES = [
  { name: "Construction", icon: Building2 },
  { name: "Hospitality", icon: Briefcase },
  { name: "Logistics", icon: ShieldCheck },
  { name: "Retail", icon: Users },
  { name: "Cleaning", icon: Check },
  { name: "Manufacturing", icon: Building2 }
];

const STEPS = [
  { num: "01", title: "Requirement Understanding", desc: "We deeply analyze your workforce needs, roles, and operational culture." },
  { num: "02", title: "Candidate Selection", desc: "Our team shortlists top-tier candidates based on skills and exact suitability." },
  { num: "03", title: "Screening & Verification", desc: "Profiles undergo strict reviews to ensure uncompromising quality and reliability." },
  { num: "04", title: "Deployment", desc: "Candidates are placed quickly and efficiently to maintain your business continuity." }
];

const WHY_CHOOSE = [
  "Access to a wide and reliable talent pool",
  "Fast turnaround and seamless deployment",
  "Quality-focused and vetted candidate selection",
  "Clear, transparent, and professional communication",
  "Consistent, dedicated support at every stage"
];

export default function WorkforceSolutions() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">
      <Navbar />

      {/* ── HERO SECTION (Solid Color, No Gradients) ── */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">
          
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              AWS Workforce Solutions
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Right People. Right Skills. <br />
            <span className="font-serif font-normal text-[#B8893A]">On Demand.</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            Delivering reliable manpower solutions designed to support your operations with speed, precision, and confidence. From skilled professionals to general labour, we connect your business with the workforce it needs to grow.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="w-full sm:w-auto">
            <Link href="/contact" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              Request Your Workforce
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── WHO WE SERVE & APPROACH (Solid Content Split) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-16 xl:py-24 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Who We Serve</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Built for businesses that demand reliability</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">We support companies across multiple industries by providing job-ready candidates aligned with your operational requirements. Whether you need short-term manpower or long-term workforce solutions, we ensure consistency and quality at every level.</p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Our Approach</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Focused on precision and long-term value</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">We believe in understanding your business before providing solutions. Our approach ensures every candidate is aligned with your expectations, helping you maintain productivity and efficiency without disruption.</p>
          </div>
        </div>
      </section>

      {/* ── WHAT WE OFFER (Clean Cards with Check Icons) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">What We Offer</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Flexible workforce solutions tailored to your needs</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            {OFFERS.map((offer, i) => (
              <div key={i} className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                    <Check className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{offer.title}</h3>
                  <p className="text-gray-500 font-light text-sm leading-relaxed">{offer.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INDUSTRIES WE SUPPORT (Lucide Icons Grid) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Industries We Support</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Versatile talent for diverse sectors</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 xl:gap-6">
            {INDUSTRIES.map((ind, i) => {
              const IconComponent = ind.icon;
              return (
                <div key={i} className="border border-gray-100 p-8 rounded-xl text-center bg-[#FAFAFA] hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col items-center justify-center group">
                  <div className="text-[#B8893A] mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 stroke-[1.5]" />
                  </div>
                  <h3 className="font-bold text-sm tracking-wide text-gray-800">{ind.name}</h3>
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-400 font-light text-sm mt-10 max-w-xl mx-auto">We understand the demands of each industry and provide candidates who are prepared to perform from day one.</p>
        </div>
      </section>

      {/* ── OUR PROCESS (Numbered Minimal Layout) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Structured. Efficient. Reliable.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 relative">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">{step.num}</div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US (Solid Checkbox Grid) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Because the right workforce makes the difference</h2>
            <p className="text-gray-500 font-light leading-relaxed">We don’t just provide manpower; we deliver workforce solutions that add value to your operational chain everyday.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-[#FAFAFA] rounded-xl">
                <Check className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="bg-[#111111] text-white py-20 xl:py-32 text-center relative">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black mb-4">Build Your Workforce with Confidence</h2>
          
         <p className="text-gray-400 font-light max-w-xl mx-auto mb-8 xl:text-lg">Let the right team drive your success. Partner with us to simplify your hiring process and secure dependable manpower tailored to your business.</p>
         <Link
              href={primaryHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-white font-bold
                         px-8 py-4 rounded-full
                         transition-all duration-300
                         hover:scale-[1.05] active:scale-[0.98]
                         shadow-lg hover:shadow-2xl"
              style={{
                background: "linear-gradient(135deg,#B8893A 0%,#D4A754 100%)",
              }}
            > */}
              {/* WhatsApp Icon */}
              {/* <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path 
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" 
                  fill="currentColor"
                />
              </svg>
              
              <span>{primaryText}</span>
            </Link>
         <Link
              href="https://wa.me/971586009983?text=I%20would%20like%20to%20schedule%20a%20call"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center
                         w-14 h-14 rounded-full
                         bg-white/10 backdrop-blur-sm
                         border-2 border-white/20
                         transition-all duration-300
                         hover:scale-110 hover:bg-white/20
                         active:scale-95
                         shadow-lg hover:shadow-2xl
                         group"
              aria-label="WhatsApp Call"
            > */}
              {/* Phone Icon */}
              {/* <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="text-white group-hover:text-[#D4A754] transition-colors duration-300"
              >
                <path 
                  d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </Link>
        </div>
      </section> */}
  <BottomCta
         title="Build Your Workforce with Confidence"
         subtitle="Let the right team drive your success. Partner with us to simplify your hiring process and secure dependable manpower tailored to your business."
       />
      <Footer />
    </main>
  );
}